/**
 * BNK KAM — Interactions & Animations
 * Scroll reveal · Parallax · System filter
 */

(function () {
  'use strict';

  /* ----------------------------------------------------------
     1. Scroll Reveal
     ---------------------------------------------------------- */
  function initScrollReveal() {
    const elements = document.querySelectorAll('[data-bnk-reveal]');
    if (!elements.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      {
        threshold: 0.12,
        rootMargin: '0px 0px -48px 0px',
      }
    );

    elements.forEach((el) => observer.observe(el));
  }

  /* ----------------------------------------------------------
     2. Parallax (subtle, performance-safe)
     ---------------------------------------------------------- */
  function initParallax() {
    const targets = document.querySelectorAll('[data-bnk-parallax]');
    if (!targets.length) return;

    let ticking = false;

    function onScroll() {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const scrollY = window.scrollY;
          targets.forEach((el) => {
            const speed = parseFloat(el.dataset.bnkParallax) || 0.15;
            const rect = el.getBoundingClientRect();
            const elTop = rect.top + scrollY;
            const offset = (scrollY - elTop) * speed;
            el.style.transform = `translateY(${offset}px)`;
          });
          ticking = false;
        });
        ticking = true;
      }
    }

    window.addEventListener('scroll', onScroll, { passive: true });
  }

  /* ----------------------------------------------------------
     3. KAM Systems Filter
     ---------------------------------------------------------- */
  function initSystemsFilter() {
    const filterBtns = document.querySelectorAll('[data-bnk-filter]');
    const systemCards = document.querySelectorAll('[data-bnk-system]');
    if (!filterBtns.length || !systemCards.length) return;

    filterBtns.forEach((btn) => {
      btn.addEventListener('click', () => {
        const target = btn.dataset.bnkFilter;

        // Update active state
        filterBtns.forEach((b) => b.classList.remove('is-active'));
        btn.classList.add('is-active');

        // Filter cards — CSS-klasse aanpak, geen setTimeout
        systemCards.forEach((card) => {
          const category = card.dataset.bnkSystem;
          const visible = target === 'all' || category === target;
          card.classList.toggle('bnk-system--hidden', !visible);
          card.classList.toggle('bnk-system--visible', visible);
        });
      });
    });
  }

  /* ----------------------------------------------------------
     4. Hero Scroll Progress Indicator
     ---------------------------------------------------------- */
  function initHeroScroll() {
    const hero = document.querySelector('.bnk-hero');
    if (!hero) return;

    const scrollLine = hero.querySelector('.bnk-hero__scroll-line');
    if (!scrollLine) return;

    let rafPending = false;
    window.addEventListener(
      'scroll',
      () => {
        if (rafPending) return;
        rafPending = true;
        requestAnimationFrame(() => {
          rafPending = false;
          const progress = Math.min(window.scrollY / window.innerHeight, 1);
          hero.style.setProperty('--scroll-progress', progress);
          if (progress > 0.1) {
            scrollLine.style.opacity = 1 - progress * 3;
          }
        });
      },
      { passive: true }
    );
  }

  /* ----------------------------------------------------------
     5. Nav Transparency on Scroll
     ---------------------------------------------------------- */
  function initNavTransparency() {
    const header = document.querySelector('header-component');
    if (!header) return;

    const threshold = 80;
    let scrolled = false;

    function update() {
      const isScrolled = window.scrollY > threshold;
      if (isScrolled !== scrolled) {
        scrolled = isScrolled;
        header.classList.toggle('bnk-nav--scrolled', scrolled);
      }
    }

    window.addEventListener('scroll', update, { passive: true });
    update();
  }

  /* ----------------------------------------------------------
     6. Smooth number counting animation
     ---------------------------------------------------------- */
  function initCounters() {
    const counters = document.querySelectorAll('[data-bnk-count]');
    if (!counters.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const el = entry.target;
          const target = parseInt(el.dataset.bnkCount, 10);
          const duration = 1400;
          const start = performance.now();

          function tick(now) {
            const elapsed = now - start;
            const progress = Math.min(elapsed / duration, 1);
            const eased = 1 - Math.pow(1 - progress, 3);
            el.textContent = Math.round(eased * target);
            if (progress < 1) requestAnimationFrame(tick);
          }

          requestAnimationFrame(tick);
          observer.unobserve(el);
        });
      },
      { threshold: 0.5 }
    );

    counters.forEach((el) => observer.observe(el));
  }

  /* ----------------------------------------------------------
     7. Contact form enhancements
     ---------------------------------------------------------- */
  function initContactForm() {
    const form = document.querySelector('.bnk-contact-form');
    if (!form) return;

    const inputs = form.querySelectorAll('.bnk-form-input, .bnk-form-textarea');
    inputs.forEach((input) => {
      input.addEventListener('focus', () => {
        input.closest('.bnk-form-group')?.classList.add('is-focused');
      });
      input.addEventListener('blur', () => {
        input.closest('.bnk-form-group')?.classList.remove('is-focused');
        if (input.value.trim()) {
          input.closest('.bnk-form-group')?.classList.add('has-value');
        } else {
          input.closest('.bnk-form-group')?.classList.remove('has-value');
        }
      });
    });
  }

  /* ----------------------------------------------------------
     8. Particle Network (Hero background)
     ---------------------------------------------------------- */
  function initParticleNetwork() {
    const canvas = document.getElementById('bnk-particles');
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const isMobile = () => window.innerWidth < 768;

    let particles = [];
    let lastFrame = 0;
    const FRAME_MS = 1000 / 30; // cap at 30fps
    const MAX_DIST = 180;
    const MAX_DIST_SQ = MAX_DIST * MAX_DIST;

    // Nebula blobs: drifting radial glows in brand colors
    const NEBULAE = [
      { x: 0.18, y: 0.35, r: 0.42, rgb: '220,20,20',   a: 0.60, sp: 0.00200, ph: 0.0 },
      { x: 0.78, y: 0.55, r: 0.48, rgb: '0,150,255',   a: 0.55, sp: 0.00230, ph: 1.6 },
      { x: 0.50, y: 0.15, r: 0.35, rgb: '0,150,255',   a: 0.50, sp: 0.00180, ph: 3.1 },
      { x: 0.88, y: 0.20, r: 0.32, rgb: '220,20,20',   a: 0.55, sp: 0.00210, ph: 4.7 },
      { x: 0.30, y: 0.75, r: 0.38, rgb: '220,20,20',   a: 0.52, sp: 0.00190, ph: 2.3 },
      { x: 0.65, y: 0.80, r: 0.36, rgb: '0,150,255',   a: 0.48, sp: 0.00220, ph: 5.5 },
    ];

    function drawNebulae(ts) {
      const minDim = Math.min(canvas.width, canvas.height);
      NEBULAE.forEach((n) => {
        const cx = (n.x + Math.sin(ts * n.sp + n.ph) * 0.07) * canvas.width;
        const cy = (n.y + Math.cos(ts * n.sp * 0.8 + n.ph) * 0.05) * canvas.height;
        const radius = n.r * minDim;
        const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, radius);
        grad.addColorStop(0,   `rgba(${n.rgb},${n.a})`);
        grad.addColorStop(0.5, `rgba(${n.rgb},${n.a * 0.4})`);
        grad.addColorStop(1,   `rgba(${n.rgb},0)`);
        ctx.beginPath();
        ctx.arc(cx, cy, radius, 0, Math.PI * 2);
        ctx.fillStyle = grad;
        ctx.fill();
      });
    }

    function resize() {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
      buildParticles();
    }

    function buildParticles() {
      const count = isMobile() ? 50 : 100;
      particles = [];
      for (let i = 0; i < count; i++) {
        particles.push({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          vx: (Math.random() - 0.5) * 0.35,
          vy: (Math.random() - 0.5) * 0.35,
          r: Math.random() * 1.6 + 0.6,
        });
      }
    }

    function draw(ts) {
      requestAnimationFrame(draw);
      if (ts - lastFrame < FRAME_MS) return;
      lastFrame = ts;

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Draw nebulae first (behind particles)
      drawNebulae(ts);

      for (let i = 0; i < particles.length; i++) {
        const p = particles[i];
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0 || p.x > canvas.width)  p.vx *= -1;
        if (p.y < 0 || p.y > canvas.height) p.vy *= -1;

        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(255,255,255,0.90)';
        ctx.fill();

        for (let j = i + 1; j < particles.length; j++) {
          const q = particles[j];
          const dx = p.x - q.x;
          const dy = p.y - q.y;
          const dSq = dx * dx + dy * dy;
          if (dSq < MAX_DIST_SQ) {
            const alpha = (1 - dSq / MAX_DIST_SQ) * 0.40;
            ctx.beginPath();
            ctx.moveTo(p.x, p.y);
            ctx.lineTo(q.x, q.y);
            ctx.strokeStyle = `rgba(255,255,255,${alpha})`;
            ctx.lineWidth = 0.8;
            ctx.stroke();
          }
        }
      }
    }

    resize();

    let resizeTimer;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(resize, 200);
    }, { passive: true });

    requestAnimationFrame(draw);
  }

  /* ----------------------------------------------------------
     9. KAM Voice Button
     ---------------------------------------------------------- */
  function initVoiceButton() {
    var btn        = document.getElementById('bnk-voice-btn');
    var audio      = document.getElementById('bnk-voice-audio');
    var helmetWrap = btn ? btn.closest('.bnk-kam-helmet-wrap') : null;
    if (!btn || !audio) return;

    var isPlaying  = false;
    var phaseTimers = [];
    var phases = ['voice-phase-1', 'voice-phase-2', 'voice-phase-3', 'voice-phase-4'];

    function clearPhases() {
      phaseTimers.forEach(function (t) { clearTimeout(t); });
      phaseTimers = [];
      if (helmetWrap) phases.forEach(function (p) { helmetWrap.classList.remove(p); });
    }

    function startPhaseSequence() {
      if (!helmetWrap) return;
      var delays = [0, 3000, 6000, 9000];
      phases.forEach(function (phase, i) {
        phaseTimers.push(setTimeout(function () {
          /* remove previous phase, add current */
          phases.forEach(function (p) { helmetWrap.classList.remove(p); });
          helmetWrap.classList.add(phase);
        }, delays[i]));
      });
    }

    function setPlaying(state) {
      isPlaying = state;
      btn.classList.toggle('is-playing', state);
      btn.setAttribute('aria-label', state ? 'Stop KAM stem' : 'Beluister de stem van KAM');
      if (state) {
        startPhaseSequence();
      } else {
        clearPhases();
      }
    }

    btn.addEventListener('click', function () {
      if (isPlaying) {
        audio.pause();
        audio.currentTime = 0;
        setPlaying(false);
      } else {
        audio.load();
        var playPromise = audio.play();
        if (playPromise !== undefined) {
          playPromise.then(function () {
            setPlaying(true);
          }).catch(function () {
            setPlaying(false);
          });
        } else {
          setPlaying(true);
        }
      }
    });

    audio.addEventListener('ended', function () {
      setPlaying(false);
    });

    audio.addEventListener('pause', function () {
      if (!audio.ended) setPlaying(false);
    });
  }

  /* ----------------------------------------------------------
     10. Lazy Video Loader — laadt video pas als hij in viewport komt
     ---------------------------------------------------------- */
  function initLazyVideos() {
    const lazyVideos = document.querySelectorAll('.bnk-lazy-video');
    if (!lazyVideos.length) return;

    // Speelt de video af op een manier die mobiele browsers accepteren.
    // iOS Safari staat autoplay alleen toe als muted + playsinline als
    // PROPERTY gezet zijn, en weigert play() vaak zolang de data nog niet
    // geladen is — daarom een nieuwe poging zodra 'canplay' vuurt.
    function tryPlay(video) {
      video.muted = true;
      video.playsInline = true;
      video.setAttribute('playsinline', '');
      video.setAttribute('webkit-playsinline', '');

      const attempt = video.play();
      if (attempt && typeof attempt.catch === 'function') {
        attempt.catch(() => {
          video.addEventListener(
            'canplay',
            () => {
              video.play().catch(() => {});
            },
            { once: true }
          );
        });
      }
    }

    const videoObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const video = entry.target;
          if (entry.isIntersecting) {
            if (!video.src && video.dataset.src) {
              video.src = video.dataset.src;
              video.load();
            }
            tryPlay(video);
          } else {
            if (!video.paused) video.pause();
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px 100px 0px' }
    );

    lazyVideos.forEach((video) => videoObserver.observe(video));
  }

  /* ----------------------------------------------------------
     11. User-gesture fallback voor video-autoplay
     Sommige mobiele situaties (iOS Low Power Mode, een geweigerde
     autoplay-policy) blokkeren elke automatische start, ook bij een
     gemute video. Bij de eerste aanraking/klik op de pagina geven we
     alle reeds geactiveerde maar gepauzeerde video's alsnog een play().
     Dit raakt de geluidsknop niet (die bestuurt een apart audio-element)
     en de video blijft gemute, dus het is veilig op iedere pagina.
     ---------------------------------------------------------- */
  function initVideoGestureFallback() {
    // Elke achtergrond- of headervideo, ongeacht welke sectie hem plaatst.
    // Bewust breed: nieuwe secties werken hierdoor vanzelf mee.
    const SELECTOR = [
      '.bnk-lazy-video',
      '.kam-video-widget__video',
      '.bnk-vakbib-hero__video',
      '[class*="-hero__video"]',
      '[class*="hero__video-el"]',
    ].join(',');

    function heroVideos() {
      return Array.prototype.slice.call(document.querySelectorAll(SELECTOR));
    }

    // Probeert af te spelen zonder ooit het geluid aan te zetten.
    function nudge(video) {
      if (!video || video.dataset.bnkPlaying === '1') return;
      if (!video.currentSrc && !video.src) {
        // Bron nog niet toegekend (lazy): zet hem alsnog, anders valt er niets te spelen.
        if (video.dataset.src) {
          video.src = video.dataset.src;
          video.load();
        } else {
          return;
        }
      }
      if (!video.paused) return;
      video.muted = true;
      video.playsInline = true;
      video.setAttribute('playsinline', '');
      video.setAttribute('webkit-playsinline', '');
      const p = video.play();
      if (p && typeof p.catch === 'function') p.catch(function () {});
    }

    function nudgeAll() {
      heroVideos().forEach(nudge);
    }

    // Zodra een video echt speelt, markeren we hem zodat we hem met rust laten.
    function markPlaying(e) {
      if (e.target && e.target.tagName === 'VIDEO') e.target.dataset.bnkPlaying = '1';
    }
    document.addEventListener('playing', markPlaying, true);

    // 1. Herkansing op elk moment waarop de browser nieuwe data heeft.
    ['loadedmetadata', 'loadeddata', 'canplay', 'canplaythrough'].forEach(function (evt) {
      document.addEventListener(
        evt,
        function (e) {
          if (e.target && e.target.tagName === 'VIDEO') nudge(e.target);
        },
        true
      );
    });

    // 2. Herkansing bij ELKE aanraking of klik, en pas stoppen wanneer alles speelt.
    //    De oude versie verwijderde zichzelf na de eerste aanraking, ook wanneer het
    //    afspelen toen nog niet kon. Eén vroege veeg maakte de fallback daardoor nutteloos.
    function onGesture() {
      nudgeAll();
      const nogGepauzeerd = heroVideos().some(function (v) {
        return (v.currentSrc || v.src) && v.paused;
      });
      if (!nogGepauzeerd) {
        ['touchstart', 'pointerdown', 'click', 'keydown', 'scroll'].forEach(function (evt) {
          document.removeEventListener(evt, onGesture, true);
        });
      }
    }
    ['touchstart', 'pointerdown', 'click', 'keydown', 'scroll'].forEach(function (evt) {
      document.addEventListener(evt, onGesture, { passive: true, capture: true });
    });

    // 3. Terugkeer naar het tabblad, en een paar late herkansingen voor trage verbindingen.
    document.addEventListener('visibilitychange', function () {
      if (!document.hidden) nudgeAll();
    });
    window.addEventListener('pageshow', nudgeAll);
    [400, 1200, 2500].forEach(function (ms) {
      setTimeout(nudgeAll, ms);
    });
  }

  /* ----------------------------------------------------------
     12. Init
     ---------------------------------------------------------- */
  function init() {
    initScrollReveal();
    initParallax();
    initSystemsFilter();
    initHeroScroll();
    initNavTransparency();
    initCounters();
    initContactForm();
    initLazyVideos();
    initVideoGestureFallback();
    initVoiceButton();

    // Particle network uitgesteld tot browser idle is — blokkeert niet de eerste paint
    if ('requestIdleCallback' in window) {
      requestIdleCallback(initParticleNetwork, { timeout: 2000 });
    } else {
      setTimeout(initParticleNetwork, 300);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

/* ── Laser scrollbar overlay (rood→blauw, gloed) ────────────── */
(function () {
  if (window.matchMedia && window.matchMedia('(max-width: 600px)').matches) return; // mobiel: native gedrag
  var docEl = document.documentElement;
  var bar = document.createElement('div');
  bar.className = 'bnk-laser-scroll';
  var thumb = document.createElement('div');
  thumb.className = 'bnk-laser-scroll__thumb';
  bar.appendChild(thumb);

  function metrics() {
    var scrollH = Math.max(docEl.scrollHeight, document.body ? document.body.scrollHeight : 0);
    var winH = window.innerHeight;
    return { scrollH: scrollH, winH: winH, trackH: winH, maxScroll: scrollH - winH };
  }

  function update() {
    var m = metrics();
    if (m.maxScroll <= 2) { bar.style.display = 'none'; return; }
    bar.style.display = '';
    var thumbH = Math.max(44, (m.winH / m.scrollH) * m.trackH);
    var y = window.scrollY || window.pageYOffset || 0;
    var top = (y / m.maxScroll) * (m.trackH - thumbH);
    thumb.style.height = thumbH + 'px';
    thumb.style.transform = 'translateY(' + Math.round(top) + 'px)';
  }

  var ticking = false;
  function onScroll() {
    if (!ticking) { window.requestAnimationFrame(function () { update(); ticking = false; }); ticking = true; }
  }

  var dragging = false, startY = 0, startScroll = 0, dMax = 0, dAvail = 1, prevBehavior = '';
  thumb.addEventListener('mousedown', function (e) {
    var m = metrics();
    var thumbH = Math.max(44, (m.winH / m.scrollH) * m.trackH);
    dMax = m.maxScroll; dAvail = Math.max(1, m.trackH - thumbH);
    dragging = true; startY = e.clientY; startScroll = window.scrollY || window.pageYOffset || 0;
    thumb.classList.add('is-dragging');
    document.body.style.userSelect = 'none';
    /* forceer direct scrollen tijdens het slepen: het thema heeft scroll-behavior:smooth,
       dat maakt elke sleepbeweging een trage animatie die achterloopt en doorloopt na loslaten */
    prevBehavior = docEl.style.scrollBehavior;
    docEl.style.scrollBehavior = 'auto';
    e.preventDefault();
  });
  window.addEventListener('mousemove', function (e) {
    if (!dragging) return;
    var scrollPerPx = dMax / dAvail;
    var target = startScroll + (e.clientY - startY) * scrollPerPx;
    if (target < 0) target = 0; else if (target > dMax) target = dMax;
    /* thumb direct met de muis meebewegen, niet wachten op de scroll-event round-trip */
    thumb.style.transform = 'translateY(' + Math.round((target / dMax) * dAvail) + 'px)';
    window.scrollTo(0, target);
  });
  window.addEventListener('mouseup', function () {
    if (!dragging) return;
    dragging = false; thumb.classList.remove('is-dragging'); document.body.style.userSelect = '';
    docEl.style.scrollBehavior = prevBehavior; /* smooth weer herstellen */
  });

  function mount() {
    if (!document.body) return;
    document.body.appendChild(bar);
    docEl.classList.add('bnk-laser-ready');
    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', update);
    var n = 0, iv = setInterval(function () { update(); if (++n > 25) clearInterval(iv); }, 400);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', mount);
  } else {
    mount();
  }
})();
